import { useState } from 'react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { ApiRequestError } from '@/lib/api';
import {
  useCancelFleetManualTask,
  useCompleteFleetManualTask,
} from '@/products/fleet/features/use-fleet';

export function FleetManualTaskActions({
  task,
}: {
  task: { id: string; title: string; subjectLabel: string };
}) {
  const complete = useCompleteFleetManualTask();
  const cancel = useCancelFleetManualTask();
  const [open, setOpen] = useState(false);
  const pending = complete.isPending || cancel.isPending;

  async function confirmCancel() {
    try {
      await cancel.mutateAsync(task.id);
      setOpen(false);
      toast.success('Aufgabe storniert.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Aufgabe konnte nicht storniert werden.',
      );
    }
  }

  return (
    <span className="flex flex-wrap items-center gap-2">
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="min-h-11"
        disabled={pending}
        onClick={() => {
          void complete.mutateAsync(task.id).then(
            () => toast.success('Aufgabe erledigt.'),
            (error: unknown) => {
              toast.error(
                error instanceof ApiRequestError
                  ? error.message
                  : 'Die Aufgabe konnte nicht erledigt werden.',
              );
            },
          );
        }}
      >
        Erledigt
      </Button>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="min-h-11"
        disabled={pending}
        onClick={() => setOpen(true)}
      >
        Stornieren
      </Button>
      <AlertDialog
        open={open}
        onOpenChange={(next) => {
          if (!next && !pending) {
            setOpen(false);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>„{task.title}“ stornieren?</AlertDialogTitle>
            <AlertDialogDescription>
              Die Aufgabe für {task.subjectLabel} erhält den Status Storniert. Die Aufgabe gilt
              nicht als erledigt, verschwindet aus den offenen Aufgaben und löst keine weitere
              Erinnerung aus.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={pending}>Abbrechen</AlertDialogCancel>
            <Button
              type="button"
              variant="destructive"
              className="min-h-11"
              disabled={pending}
              onClick={() => void confirmCancel()}
            >
              {cancel.isPending ? 'Wird storniert …' : 'Stornieren'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </span>
  );
}
